// First-party campaign attribution. Never throws — tracking must not break the UI.
//
// The touches are only written to localStorage when analytics consent is
// given (see ./consent). Without it they live in memory for this page view,
// so a form submitted in the same visit still carries its campaign.

import { CONSENT_CHANGED_EVENT, CONSENT_KEY, hasAnalyticsConsent } from "./consent";

const FIRST_KEY = "aws_attr_first";
const LAST_KEY = "aws_attr_last";
const CLAIMED_KEY = "aws_attr_claimed";
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_MAP = {
  utm_source: "source",
  utm_medium: "medium",
  utm_campaign: "campaign",
  utm_content: "content",
  utm_term: "term",
};
const CLICK_ID_MAP = { gclid: "gclid", fbclid: "fbclid", li_fat_id: "liFatId" };
const OWN_HOSTS = /(^|\.)awasource\.com$/i;
// document.referrer stays put across client-side route changes, so the
// referrer fallback only runs on the first capture of this page load.
let referrerChecked = false;
const ATTR_KEYS = [FIRST_KEY, LAST_KEY, CLAIMED_KEY];

const clip = (v, n = 200) => (typeof v === "string" ? v.trim().slice(0, n) : "");

const memory = {};

const read = (key) => {
  try {
    const raw = hasAnalyticsConsent() ? localStorage.getItem(key) : memory[key];
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.capturedAt || Date.now() - new Date(parsed.capturedAt).getTime() > TTL_MS) {
      delete memory[key];
      try { localStorage.removeItem(key); } catch (_) {}
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

const write = (key, value) => {
  const raw = JSON.stringify(value);
  memory[key] = raw;
  if (!hasAnalyticsConsent()) return;
  try {
    localStorage.setItem(key, raw);
  } catch (_) {
    // storage blocked (private mode, etc.) — memory copy still works
  }
};

// Consent given later in the visit: persist what was captured so far.
// Consent refused: remove anything stored earlier.
if (typeof window !== "undefined") {
  try {
    window.addEventListener(CONSENT_CHANGED_EVENT, (e) => {
      try {
        if (e?.detail?.analytics) {
          Object.entries(memory).forEach(([key, raw]) => localStorage.setItem(key, raw));
        } else {
          ATTR_KEYS.forEach((key) => {
            const raw = localStorage.getItem(key);
            if (raw && !memory[key]) memory[key] = raw;
            localStorage.removeItem(key);
          });
        }
      } catch (_) {}
    });
  } catch (_) {}
}

function touchFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    const touch = {};
    Object.entries(UTM_MAP).forEach(([param, key]) => {
      const v = clip(params.get(param));
      if (v) touch[key] = v.toLowerCase();
    });
    Object.entries(CLICK_ID_MAP).forEach(([param, key]) => {
      const v = clip(params.get(param), 300);
      if (v) touch[key] = v;
    });

    // No campaign params: fall back to an external referrer (e.g. a partner site).
    // Later route changes in the same document are internal and must not
    // overwrite a stored touch with that same, stale referrer.
    if (!Object.keys(touch).length) {
      if (referrerChecked) return null;
      referrerChecked = true;
      const ref = document.referrer ? new URL(document.referrer) : null;
      if (!ref || OWN_HOSTS.test(ref.hostname)) return null; // direct or internal — don't record
      touch.source = ref.hostname.replace(/^www\./, "").toLowerCase();
      touch.medium = "referral";
    }
    referrerChecked = true;

    // Infer source/medium from click IDs when UTMs are missing.
    if (!touch.source && touch.gclid) Object.assign(touch, { source: "google", medium: "cpc" });
    if (!touch.source && touch.fbclid) Object.assign(touch, { source: "facebook", medium: "paid_social" });
    if (!touch.source && touch.liFatId) Object.assign(touch, { source: "linkedin", medium: "paid_social" });

    touch.landingPage = window.location.pathname;
    try {
      if (document.referrer) {
        const refHost = new URL(document.referrer).hostname;
        if (!OWN_HOSTS.test(refHost)) touch.referrer = refHost;
      }
    } catch (_) {}
    touch.capturedAt = new Date().toISOString();
    return touch;
  } catch (_) {
    return null;
  }
}

/** Call on every route change. Direct/internal visits never overwrite a stored touch. */
export function captureAttribution() {
  const touch = touchFromLocation();
  if (!touch) return;
  if (!read(FIRST_KEY)) write(FIRST_KEY, touch);
  write(LAST_KEY, touch);
}

/** `{ firstTouch, lastTouch }` or undefined — spread into signup/purchase payloads. */
export function getAttribution() {
  const firstTouch = read(FIRST_KEY);
  const lastTouch = read(LAST_KEY);
  if (!firstTouch && !lastTouch) return undefined;
  return { firstTouch: firstTouch || lastTouch, lastTouch: lastTouch || firstTouch };
}

/** For forms whose backend expects the flat FormData shape (partnership enquiry, brochure). */
export function getFormUtm() {
  const t = read(LAST_KEY) || read(FIRST_KEY);
  if (!t) return {};
  const { source, medium, campaign, content, term, landingPage } = t;
  return { utm: { source, medium, campaign, content, term }, landingPage };
}

export const markAttributionClaimed = () => write(CLAIMED_KEY, { capturedAt: new Date().toISOString() });
export const isAttributionClaimed = () => !!read(CLAIMED_KEY);

// Logout and a 401 wipe localStorage. Keep the campaign so a later signup
// or purchase in this browser is still attributable, and keep the visitor's
// cookie choice so the banner doesn't come back.
export function clearStorageKeepingAttribution() {
  try {
    const kept = [...ATTR_KEYS, CONSENT_KEY].map((key) => [key, localStorage.getItem(key)]);
    localStorage.clear();
    kept.forEach(([key, value]) => {
      if (value) localStorage.setItem(key, value);
    });
  } catch (_) {
    // ignore
  }
}
